// src/toolkitServices/goldSchemeApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const pickRows = (res) => {
  // supports: {data: []}, {items: []}, {rows: []}, {data: {rows: []}}
  if (!res) return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.rows)) return res.rows;
  if (Array.isArray(res?.data?.rows)) return res.data.rows;
  return [];
};

const goldSchemeApi = createApi({
  reducerPath: "goldSchemeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["GoldScheme", "GoldSchemeEnrollment"],
  endpoints: (builder) => ({
    /* ------------------------------------------------------------------
       GOLD SCHEME MASTER
       ------------------------------------------------------------------ */

    addGoldScheme: builder.mutation({
      query: (data) => ({
        url: "/gold-schemes",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "GoldScheme", id: "LIST" }],
    }),

    getAllGoldSchemes: builder.query({
      query: ({
        page = 1,
        limit = 50,
        search = "",
        isActive,
        branchId,
        schemeType,
        metalType,
        tenantId,
      } = {}) => {
        const params = { page, limit, search };
        if (typeof isActive === "boolean") params.isActive = isActive;
        if (branchId) params.branchId = branchId;
        if (schemeType) params.schemeType = schemeType;
        if (metalType) params.metalType = metalType;
        if (tenantId) params.tenantId = tenantId;

        return { url: "/gold-schemes", params };
      },
      providesTags: (res) => {
        const rows = pickRows(res);
        return [
          { type: "GoldScheme", id: "LIST" },
          ...rows.map((x) => ({ type: "GoldScheme", id: x?._id || x?.id })),
        ];
      },
    }),

    getGoldSchemeById: builder.query({
      query: ({ id, branchId, tenantId }) => {
        const params = {};
        if (branchId) params.branchId = branchId;
        if (tenantId) params.tenantId = tenantId;
        return { url: `/gold-schemes/${id}`, params };
      },
      providesTags: (_res, _err, { id }) => [{ type: "GoldScheme", id }],
    }),

    updateGoldScheme: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/gold-schemes/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "GoldScheme", id },
        { type: "GoldScheme", id: "LIST" },
      ],
    }),

    toggleGoldSchemeActive: builder.mutation({
      query: ({ id, branchId, tenantId }) => {
        const params = {};
        if (branchId) params.branchId = branchId;
        if (tenantId) params.tenantId = tenantId;

        return {
          url: `/gold-schemes/${id}/toggle-active`,
          method: "PATCH",
          params,
        };
      },
      invalidatesTags: (_res, _err, { id }) => [
        { type: "GoldScheme", id },
        { type: "GoldScheme", id: "LIST" },
      ],
    }),

    deleteGoldScheme: builder.mutation({
      query: ({ id, branchId, tenantId }) => {
        const params = {};
        if (branchId) params.branchId = branchId;
        if (tenantId) params.tenantId = tenantId;

        return { url: `/gold-schemes/${id}`, method: "DELETE", params };
      },
      invalidatesTags: (_res, _err, { id }) => [
        { type: "GoldScheme", id },
        { type: "GoldScheme", id: "LIST" },
      ],
    }),

    /* ------------------------------------------------------------------
       GOLD SCHEME ENROLLMENTS
       ------------------------------------------------------------------ */

    addGoldSchemeEnrollment: builder.mutation({
      query: (data) => ({
        url: "/gold-schemes/enroll",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "GoldSchemeEnrollment", id: "LIST" }],
    }),

    getAllGoldSchemeEnrollments: builder.query({
      query: ({
        page = 1,
        limit = 50,
        search = "",
        isActive,
        branchId,
        tenantId,
        schemeType,
        metalType,
        schemeId,
        customerId,
        status,
      } = {}) => {
        const params = { page, limit, search };

        if (typeof isActive === "boolean") params.isActive = isActive;
        if (branchId) params.branchId = branchId;
        if (tenantId) params.tenantId = tenantId;
        if (schemeType) params.schemeType = schemeType;
        if (metalType) params.metalType = metalType; // backend supports then ok
        if (schemeId) params.schemeId = schemeId;
        if (customerId) params.customerId = customerId;
        if (status) params.status = status;

        return { url: "/gold-schemes/enrollments", params };
      },
      providesTags: (res) => {
        const rows = pickRows(res);
        return [
          { type: "GoldSchemeEnrollment", id: "LIST" },
          ...rows.map((x) => ({
            type: "GoldSchemeEnrollment",
            id: x?._id || x?.id,
          })),
        ];
      },
    }),

    getGoldSchemeEnrollmentById: builder.query({
      query: ({ id, branchId, tenantId }) => {
        const params = {};
        if (branchId) params.branchId = branchId;
        if (tenantId) params.tenantId = tenantId;

        return { url: `/gold-schemes/enrollments/${id}`, params };
      },
      providesTags: (_res, _err, { id }) => [{ type: "GoldSchemeEnrollment", id }],
    }),

    updateGoldSchemeEnrollment: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/gold-schemes/enrollments/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "GoldSchemeEnrollment", id },
        { type: "GoldSchemeEnrollment", id: "LIST" },
      ],
    }),

    toggleGoldSchemeEnrollmentActive: builder.mutation({
      query: ({ id, branchId, tenantId }) => {
        const params = {};
        if (branchId) params.branchId = branchId;
        if (tenantId) params.tenantId = tenantId;

        return {
          url: `/gold-schemes/enrollments/${id}/toggle-active`,
          method: "PATCH",
          params,
        };
      },
      invalidatesTags: (_res, _err, { id }) => [
        { type: "GoldSchemeEnrollment", id },
        { type: "GoldSchemeEnrollment", id: "LIST" },
      ],
    }),

    deleteGoldSchemeEnrollment: builder.mutation({
      query: ({ id, branchId, tenantId }) => {
        const params = {};
        if (branchId) params.branchId = branchId;
        if (tenantId) params.tenantId = tenantId;

        return { url: `/gold-schemes/enrollments/${id}`, method: "DELETE", params };
      },
      invalidatesTags: (_res, _err, { id }) => [
        { type: "GoldSchemeEnrollment", id },
        { type: "GoldSchemeEnrollment", id: "LIST" },
      ],
    }),
  }),
});

export const {
  // Schemes
  useAddGoldSchemeMutation,
  useGetAllGoldSchemesQuery,
  useGetGoldSchemeByIdQuery,
  useUpdateGoldSchemeMutation,
  useToggleGoldSchemeActiveMutation,
  useDeleteGoldSchemeMutation,

  // Enrollments
  useAddGoldSchemeEnrollmentMutation,
  useGetAllGoldSchemeEnrollmentsQuery,
  useGetGoldSchemeEnrollmentByIdQuery,
  useUpdateGoldSchemeEnrollmentMutation,
  useToggleGoldSchemeEnrollmentActiveMutation,
  useDeleteGoldSchemeEnrollmentMutation,
} = goldSchemeApi;

export default goldSchemeApi;
